import { matchesEveryWord } from '@cpce/shared';
import { CheckIcon, ChevronsUpDownIcon, Trash2Icon } from 'lucide-react';
import * as React from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/shadcn/Command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/Popover';
import { cn } from '@/utils/style';
import { Input } from './Input';
import { LoadingLabel } from './LoadingLabel';

export type ComboboxOptions<T extends number | string> = {
  value: T;
  label: string;
  /** Adds a red bin on the row, for lists the user has fed themselves. */
  deletable?: boolean;
  /** A status shown at the end of the row, outside the searched label. */
  badge?: string;
}[];

interface ComboboxProps<T extends number | string> {
  options: ComboboxOptions<T>;
  value: T | undefined;
  onChange: (value: T) => void;
  onReselect?: () => void;
  onSearchChange?: (search: string) => void;
  /** Called when the list is scrolled to its end: lets a server-paginated picker load more. */
  onScrollEnd?: () => void;
  onDeleteOption?: (value: T) => void;
  disabled?: boolean;
  error?: string;
  /**
   * Shown when there is nothing to pick at all, in place of the search-came-back-empty wording.
   * A list that is empty because nothing was ever configured is not a failed search, and telling
   * the user "aucun résultat" sends them looking for a typo instead of at the missing setup.
   */
  emptyMessage?: string;
  /** While the options are being fetched: an empty list then means « wait », not « nothing ». */
  isLoading?: boolean;
}

export function Combobox<T extends number | string>(props: ComboboxProps<T>) {
  const {
    options,
    value,
    onChange,
    onReselect,
    onSearchChange,
    onScrollEnd,
    onDeleteOption,
    disabled = false,
    error,
    emptyMessage,
    isLoading = false,
  } = props;
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const noOptionsAtAll = !isLoading && options.length === 0 && emptyMessage !== undefined;
  const filteredOptions = options.filter((option) => {
    if (option.value === value) return true;
    return matchesEveryWord(option.label, search);
  });

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          aria-expanded={open}
          className={cn('w-full justify-between', error && 'border-red-500')}
          disabled={disabled}
          aria-invalid={error ? 'true' : undefined}
        >
          <span className="truncate">
            {value ? options.find((option) => option.value === value)?.label : 'Sélectionner'}
          </span>
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Command shouldFilter={false}>
          {!noOptionsAtAll && (
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                onSearchChange?.(e.target.value);
              }}
              noBorder
            />
          )}
          <CommandList
            onWheel={(e) => {
              e.stopPropagation();
            }}
            onScroll={(e) => {
              const list = e.currentTarget;
              if (list.scrollHeight - list.scrollTop - list.clientHeight < 40) onScrollEnd?.();
            }}
          >
            <CommandEmpty
              className={cn(
                'py-6 text-center text-sm',
                noOptionsAtAll && 'max-w-xs text-balance px-4 text-gray-600'
              )}
            >
              {isLoading ? (
                <LoadingLabel />
              ) : noOptionsAtAll ? (
                emptyMessage
              ) : (
                'Aucun résultat trouvé.'
              )}
            </CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value.toString()}
                  onSelect={(newString) => {
                    const newOption = options.find(
                      (option) => option.value.toString() === newString
                    );
                    if (!newOption) return;
                    if (newOption.value === value && onReselect) {
                      onReselect();
                    } else {
                      onChange(newOption.value);
                    }
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn('h-4 w-4', value === option.value ? 'opacity-100' : 'opacity-0')}
                  />
                  <span className="flex-1">{option.label}</span>
                  {option.badge && <Badge>{option.badge}</Badge>}
                  {option.deletable && onDeleteOption && (
                    <button
                      type="button"
                      title="Supprimer"
                      className="cursor-pointer text-red-600 hover:opacity-75"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDeleteOption(option.value);
                      }}
                    >
                      <Trash2Icon className="h-4 w-4" />
                    </button>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
            {isLoading && filteredOptions.length > 0 && (
              <div className="py-2 text-center text-sm">
                <LoadingLabel />
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
